import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { useAppSelector } from "app/hooks";
import { selectUserIsAdminOrUserAdmin, selectUserIsSalesUser } from "features/auth/authSlice";
import OrderSummary from "./OrderSummary";
import CustomerSummary from "./CustomerSummary";
import ContractData from "./ContractData";
import OutlinedCard from "components/OutlinedCard";
import { useOrderContext } from "context/OrderContext";
import { useSetFollowUpDoneMutation } from "app/api/orders";
import ReferenceSection from "./ReferenceSection";
import ResponsiveSection from "components/ResponsiveSection";
import ProjectGroupSection from "./ProjectGroupSection";
import ExportFinalReportDialog from "../dialogs/ExportFinalReportDialog";
import ExportProductsListDialog from "../dialogs/ExportProductsListDialog";
import SubcontractorSection from "./SubcontractorSection";
import { useAuthContext } from "features/auth/AuthContext";
import ReviewSection from "./ReviewSection";

const OrderInfo: React.FC = () => {
  const { user } = useAuthContext();
  const order = useOrderContext();
  const isAdminOrUserAdmin = useAppSelector(selectUserIsAdminOrUserAdmin);
  const isSales = useAppSelector(selectUserIsSalesUser);
  const [showExportFinalReportDialog, setShowExportFinalReportDialog] = useState(false);
  const [showExportProductsListDialog, setShowExportProductsListDialog] = useState(false);

  const [setIsFollowUpDone] = useSetFollowUpDoneMutation();

  const toggleTelemarketingDone = () => {
    setIsFollowUpDone({
      orderId: order.ordernr,
      followUpDone: !order.customerFollowUp,
    });
  };

  return (
    <OutlinedCard sx={{ mb: 1, p: 0 }}>
      <OrderSummary />
      <Divider color="grey" />

      <CustomerSummary />
      <Divider color="grey" />

      <ResponsiveSection title={<Typography fontSize="inherit">Projektgrupp</Typography>}>
        <Box px={2} pb={1}>
          <ProjectGroupSection order={order} />
        </Box>
      </ResponsiveSection>
      <Divider color="grey" />

      {!order.parentOrderNumber && (
        <>
          <ContractData />
          <Divider color="grey" />

          <ResponsiveSection title={<Typography fontSize="inherit">Underentreprenör</Typography>}>
            <SubcontractorSection order={order} />
          </ResponsiveSection>
          <Divider color="grey" />

          <ResponsiveSection title={<Typography fontSize="inherit">Referensobjekt</Typography>}>
            <ReferenceSection orderId={order.ordernr} customerReference={order.customerReference} />
          </ResponsiveSection>
          <Divider color="grey" />

          {user?.isOrderReviewer && (
            <>
              <ResponsiveSection title={<Typography fontSize="inherit">Granskning</Typography>}>
                <ReviewSection order={order} />
              </ResponsiveSection>
              <Divider color="grey" />
            </>
          )}

          <Box
            sx={(theme) => ({
              display: "grid",
              gridTemplateRows: "auto auto",
              p: 1,
              gap: 1,
              [theme.breakpoints.up("md")]: {
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
                flexWrap: "wrap",
              },
            })}
          >
            {(isAdminOrUserAdmin || isSales) && (
              <Box
                sx={(theme) => ({
                  display: "grid",
                  [theme.breakpoints.down("md")]: {
                    px: 1,
                  },
                  [theme.breakpoints.up("md")]: {
                    gridAutoFlow: "column",
                  },
                })}
              >
                <FormControlLabel
                  control={
                    <Checkbox checked={order.customerFollowUp} onChange={toggleTelemarketingDone} />
                  }
                  label="Telemarketing utförd"
                  labelPlacement="end"
                />
              </Box>
            )}

            <Box
              sx={(theme) => ({
                display: "grid",
                gap: 1,
                [theme.breakpoints.up("xs")]: {
                  gridTemplateColumns: "1fr 1fr",
                },
              })}
            >
              <Tooltip
                arrow
                title="Generera en sammanställning av alla produkter som tillhör ordern i Excel-format (.xlsx). Inkludera alla, eller välj specifika checklistor."
              >
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => setShowExportProductsListDialog(true)}
                >
                  Produktlista
                </Button>
              </Tooltip>
              <Tooltip
                arrow
                title="Generera ett PDF-dokument med info & bilder att lämna till kund efter installation. Inkludera alla, eller välj specifika checklistor."
              >
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => setShowExportFinalReportDialog(true)}
                >
                  Slutrapport
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </>
      )}

      {showExportFinalReportDialog && (
        <ExportFinalReportDialog onClose={() => setShowExportFinalReportDialog(false)} />
      )}

      {showExportProductsListDialog && (
        <ExportProductsListDialog onClose={() => setShowExportProductsListDialog(false)} />
      )}
    </OutlinedCard>
  );
};

export default OrderInfo;
